import { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Play, 
  Headphones, 
  Clock, 
  Star, 
  ShoppingCart,
  User,
  CheckCircle,
  ArrowCounterClockwise
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useAudioPlayer } from '@/contexts/AudioPlayerContext';
import { checkCacheStatus } from '@/utils/audioCache';
import api from '@/lib/api';
import { toast } from 'sonner';
import SEO from '@/components/SEO';
import { getGuestTourIds } from '@/lib/guestPurchases';
import RestorePurchasesModal from '@/components/RestorePurchasesModal';

const AudioTours = () => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [purchasedTours, setPurchasedTours] = useState([]);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [pendingPurchaseTour, setPendingPurchaseTour] = useState(null);
  const [cachedTours, setCachedTours] = useState({});
  const [restoreOpen, setRestoreOpen] = useState(false);
  
  const { playTrack, currentTrack, isPlaying } = useAudioPlayer();
  const navigate = useNavigate();

  // Shared purchase flow function - redirect to embedded checkout page
  const handlePurchaseFlow = useCallback(async (tour) => {
    // Navigate to embedded checkout page (keeps nav bar and site frame)
    navigate(`/checkout/${tour.tour_id}`);
  }, [navigate]);

  // Hydrate guest purchases from localStorage immediately on mount so the
  // "Purchased" / "Play" UI lights up before any network call.
  useEffect(() => {
    const guestIds = getGuestTourIds();
    if (guestIds.length > 0) {
      setPurchasedTours((prev) => Array.from(new Set([...prev, ...guestIds])));
    }
  }, []);

  // Check for existing auth token
  useEffect(() => {
    const token = localStorage.getItem('customer_token');
    const userData = localStorage.getItem('customer_user');
    if (token && userData) {
      setUser(JSON.parse(userData));
      fetchPurchasedTours(token);
    }
  }, []);

  // Check for pending purchase after login redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const action = urlParams.get('action');
    const pendingTourId = localStorage.getItem('pending_purchase_tour');
    
    console.log('Checking pending purchase:', { action, pendingTourId, user: !!user, toursCount: tours.length });
    
    if (action === 'purchase' && pendingTourId && user && tours.length > 0) {
      // Find the tour and trigger purchase
      const tour = tours.find(t => t.tour_id === pendingTourId);
      console.log('Found pending tour:', tour);
      if (tour) {
        localStorage.removeItem('pending_purchase_tour');
        window.history.replaceState({}, '', window.location.pathname);
        // Trigger purchase flow
        toast.info('Redirecting to payment...');
        handlePurchaseFlow(tour);
      }
    }
  }, [user, tours, handlePurchaseFlow]);

  // Check for payment completion from URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const success = urlParams.get('success');
    const cancelled = urlParams.get('cancelled');
    
    if (sessionId && success === 'true') {
      // Poll for payment status
      pollPaymentStatus(sessionId);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (cancelled === 'true') {
      toast.error('Payment was cancelled');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;
    
    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please refresh the page.');
      return;
    }
    
    try {
      const token = localStorage.getItem('customer_token');
      if (!token) return;
      
      const response = await api.get(`/audio-tours/checkout/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.payment_status === 'paid') {
        toast.success('🎉 Payment successful! You can now listen to the full tour.');
        if (response.data.tour_id) {
          setPurchasedTours(prev => [...prev, response.data.tour_id]);
        }
        return;
      }
      
      // Continue polling if still pending
      if (response.data.status !== 'expired') {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  useEffect(() => {
    fetchTours();
  }, []);

  // Check cache status for offline badges
  useEffect(() => {
    if (tours.length > 0 && purchasedTours.length > 0) {
      const purchasedTourData = tours.filter(t => purchasedTours.includes(t.tour_id));
      const urls = purchasedTourData.map(t => t.audio_url).filter(Boolean);
      if (urls.length > 0) {
        checkCacheStatus(urls).then(setCachedTours);
      }
    }
  }, [tours, purchasedTours]);

  const fetchTours = async () => {
    try {
      const response = await api.get('/audio-tours');
      setTours(response.data);
    } catch (error) {
      console.error('Error fetching tours:', error);
      toast.error('Failed to load audio tours');
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchasedTours = async (token) => {
    try {
      const response = await api.get('/auth/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPurchasedTours(response.data.purchased_tours || []);
    } catch (error) {
      console.error('Error fetching purchased tours:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_user');
    setUser(null);
    setPurchasedTours([]);
    toast.success('Logged out successfully');
  };

  const handlePurchase = async (tour) => {
    // Guest checkout — no auth required. The /checkout page handles
    // logged-in users transparently (the backend pre-links the purchase
    // to their account if a JWT is sent).
    handlePurchaseFlow(tour);
  };

  const handlePlayTour = async (tour) => {
    const isPurchased = purchasedTours.includes(tour.tour_id);
    
    // If purchased, play full audio; otherwise play preview
    const trackToPlay = {
      ...tour,
      audio_url: isPurchased ? tour.audio_url : tour.preview_url,
      is_preview: !isPurchased
    };
    
    playTrack(trackToPlay);
  };

  // Play full tour (for purchased tours only) - opens in full screen player
  const handlePlayFullTour = async (tour) => {
    const trackToPlay = {
      ...tour,
      audio_url: tour.audio_url,
      is_preview: false
    };
    
    // Play and open full screen player directly
    playTrack(trackToPlay, true);
  };

  const isPurchased = (tourId) => purchasedTours.includes(tourId);
  const isCurrentlyPlaying = (tourId) => currentTrack?.tour_id === tourId && isPlaying;

  return (
    <>
      <SEO
        title="Audio Tours - Immersive Winery Experiences"
        description="Immersive audio tours of Napa Valley and Sonoma wineries. Listen to expert narrated guides as you explore wine country. 30-second free previews available."
        path="/audio-tours"
        image="https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?w=1200"
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Audio Tours', path: '/audio-tours' }
        ]}
      />

      {/* Hero */}
      <section 
        className="pt-24 sm:pt-32 pb-12 sm:pb-16 bg-gradient-to-b from-wine-900 to-wine-800"
        data-testid="audio-tours-hero"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <Headphones size={20} className="text-gold-400" />
              <span className="text-white/90 text-sm font-medium">Immersive Audio Experiences</span>
            </div>
            
            <h1 className="font-serif text-3xl sm:text-4xl md:text-6xl text-white mb-4">
              Audio <span className="italic text-gold-400">Tours</span>
            </h1>
            <p className="text-white/70 text-sm sm:text-base max-w-2xl mx-auto px-4">
              Expert-narrated audio tours of Napa Valley's finest wineries. Listen as you explore and discover the rich history, wine-making secrets, and hidden gems.
            </p>
            
            {/* User Info or Login */}
            <div className="mt-6">
              {user ? (
                <div className="flex items-center justify-center gap-4">
                  <span className="text-white/80 text-sm">
                    Welcome, <span className="text-gold-400 font-medium">{user.name}</span>
                  </span>
                  <button
                    onClick={handleLogout}
                    className="text-white/60 hover:text-white text-sm underline"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  <Button
                    onClick={() => setRestoreOpen(true)}
                    variant="outline"
                    className="bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white"
                    data-testid="restore-purchases-btn"
                  >
                    <ArrowCounterClockwise size={16} className="mr-2" />
                    Restore purchases
                  </Button>
                  <Button
                    onClick={() => navigate('/signin?redirect=/audio-tours')}
                    className="bg-gold-500 hover:bg-gold-400 text-wine-900"
                    data-testid="login-btn"
                  >
                    <User size={18} className="mr-2" />
                    Sign in
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>


      {/* Tours Grid */}
      <section className="py-12 sm:py-16" data-testid="tours-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-muted animate-pulse rounded-xl h-96" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {tours.map((tour, index) => (
                <motion.div
                  key={tour.tour_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index * 0.1, 0.5) }}
                  className="bg-card rounded-xl overflow-hidden border border-border/50 shadow-sm hover:shadow-lg transition-all group"
                  data-testid={`tour-card-${tour.tour_id}`}
                >
                  {/* Cover Image */}
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={tour.cover_image_url || 'https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?w=600'}
                      alt={tour.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                    
                    {/* Play Button */}
                    <button
                      onClick={() => handlePlayTour(tour)}
                      className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full flex items-center justify-center transition-all ${
                        isCurrentlyPlaying(tour.tour_id)
                          ? 'bg-gold-500 scale-100'
                          : 'bg-white/90 hover:bg-white scale-90 group-hover:scale-100'
                      }`}
                      data-testid={`play-tour-${tour.tour_id}`}
                    >
                      {isCurrentlyPlaying(tour.tour_id) ? (
                        <div className="flex gap-1">
                          {[...Array(3)].map((_, i) => (
                            <div
                              key={i}
                              className="w-1 bg-wine-900 rounded-full animate-pulse"
                              style={{ 
                                height: `${12 + Math.random() * 12}px`,
                                animationDelay: `${i * 0.15}s`
                              }}
                            />
                          ))}
                        </div>
                      ) : (
                        <Play size={28} weight="fill" className="text-wine-900 ml-1" />
                      )}
                    </button>
                    
                    {/* Duration Badge */}
                    <div className="absolute bottom-3 left-3 flex items-center gap-1 bg-black/60 backdrop-blur-sm rounded-full px-2 py-1 text-white text-xs">
                      <Clock size={12} />
                      {tour.duration_minutes} min
                    </div>
                    
                    {/* Purchased Badge - top left of image */}
                    {isPurchased(tour.tour_id) && (
                      <div className="absolute top-3 left-3 flex items-center gap-1 bg-green-500 rounded-full px-2.5 py-1 text-white text-xs font-medium" data-testid={`purchased-badge-${tour.tour_id}`}>
                        <CheckCircle size={14} weight="fill" />
                        Purchased
                      </div>
                    )}

                    {/* Offline Badge - top right of image */}
                    {isPurchased(tour.tour_id) && cachedTours[tour.audio_url] && (
                      <div className="absolute top-3 right-3 flex items-center gap-1 bg-blue-500 rounded-full px-2.5 py-1 text-white text-xs font-medium cursor-default" title="Can Play Offline" data-testid={`offline-badge-${tour.tour_id}`}>
                        <CheckCircle size={14} weight="fill" />
                        Offline ready
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex items-center">
                        <Star size={14} weight="fill" className="text-gold-500" />
                        <span className="text-sm font-medium ml-1">{tour.rating}</span>
                      </div>
                      {tour.display_purchases > 0 && (
                        <>
                          <span className="text-muted-foreground text-sm">•</span>
                          <span className="text-muted-foreground text-sm">{tour.display_purchases} purchased</span>
                        </>
                      )}
                    </div>
                    
                    {/* Title wrapping: use foundational CSS primitives
                        instead of `line-clamp-1` (which Tailwind can purge
                        from production builds when not used elsewhere in
                        the bundle). On mobile titles flow to as many lines
                        as needed so long names like "Castello di Amorosa
                        Cabernet Tour" stay fully readable; on desktop the
                        2-line clamp is reinstated via plain CSS to keep
                        card heights consistent in the grid. */}
                    <Link
                      to={`/audio-tours/${tour.tour_id}`}
                      className="block group/title"
                      data-testid={`tour-title-link-${tour.tour_id}`}
                    >
                      <h3
                        className="font-serif text-lg text-foreground mb-1 break-words sm:overflow-hidden sm:[display:-webkit-box] sm:[-webkit-line-clamp:2] sm:[-webkit-box-orient:vertical] group-hover/title:text-[#722F37] transition-colors"
                        title={tour.title}
                      >
                        {tour.title}
                      </h3>
                    </Link>
                    <p className="text-muted-foreground text-sm mb-3">
                      {tour.winery_name}
                    </p>
                    
                    {/* Full Description - render HTML from rich text editor */}
                    <div 
                      className="text-muted-foreground text-sm mb-4 prose prose-sm prose-wine max-w-none"
                      dangerouslySetInnerHTML={{ __html: tour.description }}
                    />
                    
                    {/* Highlights */}
                    {tour.highlights?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-4">
                        {tour.highlights.slice(0, 3).map((highlight, i) => (
                          <span
                            key={i}
                            className="bg-muted text-muted-foreground text-xs px-2 py-1 rounded-full"
                          >
                            {highlight}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {/* Price & Actions */}
                    <div className="flex items-center justify-between pt-4 border-t border-border/50">
                      {isPurchased(tour.tour_id) ? (
                        <div className="flex gap-2 w-full">
                          {/* Play Button - Green, full width */}
                          <Button
                            onClick={() => handlePlayFullTour(tour)}
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Play size={16} weight="fill" className="mr-1" />
                            Play
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div>
                            <span className="text-2xl font-bold text-foreground">
                              ${tour.price ? tour.price.toFixed(2) : '0.00'}
                            </span>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handlePlayTour(tour)}
                              className="text-xs"
                            >
                              <Play size={14} className="mr-1" />
                              Preview
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handlePurchase(tour)}
                              className="bg-gold-500 hover:bg-gold-400 text-wine-900"
                              disabled={processingPayment}
                            >
                              {processingPayment ? (
                                <>Processing...</>
                              ) : (
                                <>
                                  <ShoppingCart size={14} className="mr-1" />
                                  Buy
                                </>
                              )}
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 sm:py-16 bg-muted/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-serif text-2xl sm:text-3xl text-foreground mb-8">
            How Audio Tours Work
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Headphones, title: "Preview Free", desc: "Listen to a 30-second preview of any tour before purchasing" },
              { icon: ShoppingCart, title: "Purchase Once", desc: "Buy the full tour and access it anytime, even offline" },
              { icon: Play, title: "Listen Anywhere", desc: "Enjoy immersive audio as you explore wine country" }
            ].map((step, i) => (
              <div key={i} className="bg-card rounded-lg p-6 border border-border/50">
                <div className="w-12 h-12 rounded-full bg-wine-100 flex items-center justify-center mx-auto mb-4">
                  <step.icon size={24} className="text-wine-600" />
                </div>
                <h3 className="font-medium text-foreground mb-2">{step.title}</h3>
                <p className="text-muted-foreground text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <RestorePurchasesModal open={restoreOpen} onClose={() => setRestoreOpen(false)} />
    </>
  );
};

export default AudioTours;
